import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/** Minimum horizontal travel, in pixels, before a swipe changes slide. */
const SWIPE_THRESHOLD = 40;

/**
 * A "featured in press" carousel: stacked quote panels selected by a rail of publication logos.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} stage - The grid that stacks every panel in one cell.
 * @property {HTMLElement} [rail] - The tablist of logo buttons. Absent when there is a single block.
 * @property {HTMLElement[]} [tabs] - The logo buttons.
 * @property {HTMLElement[]} [panels] - The quote panels, in block order.
 *
 * @extends {Component<Refs>}
 */
class PressCarousel extends Component {
  requiredRefs = ['stage'];

  /** @type {number} Index of the committed slide (hover previews do not change it). */
  #current = 0;

  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #listeners = null;

  /** @type {{ id: number, x: number, y: number } | null} */
  #swipe = null;

  #visible = true;
  #hovered = false;
  #focused = false;
  /** Set once the visitor deliberately interacts — autoplay never restarts after that. */
  #engaged = false;
  /** Set while the merchant has a block selected in the theme editor. */
  #editing = false;

  connectedCallback() {
    super.connectedCallback();

    const tabs = this.#tabs;
    const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#current = selected === -1 ? 0 : selected;

    // Guard against a re-entrant connect (the element being moved rather than recreated),
    // which would otherwise stack a second set of listeners on top of the first.
    this.#listeners?.abort();
    const { signal } = (this.#listeners = new AbortController());

    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal });
    this.refs.stage.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.refs.stage.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.refs.stage.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    if (this.#canAutoplay) {
      this.#observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.#visible = entry.isIntersecting;
          this.#sync();
        },
        { threshold: 0.1 }
      );
      this.#observer.observe(this);
    }

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#swipe = null;
  }

  /**
   * Activates the slide belonging to the clicked logo. Wired up declaratively with `on:click="/select"`.
   *
   * @param {Event} event
   */
  select(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    this.#engage();
    this.#activate(Number(button.dataset.index));
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return this.refs.tabs ?? [];
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return this.refs.panels ?? [];
  }

  get #canAutoplay() {
    return this.dataset.autoplay === 'true' && this.#panels.length > 1 && !prefersReducedMotion();
  }

  /**
   * Shows a slide.
   *
   * @param {number} index - Index to show. Wraps around both ends.
   * @param {boolean} [commit] - When false the change is a transient hover preview, so focus order
   *   and the remembered slide are left alone.
   */
  #activate(index, commit = true) {
    const panels = this.#panels;
    if (!panels.length || Number.isNaN(index)) return;

    const next = ((index % panels.length) + panels.length) % panels.length;
    const tabs = this.#tabs;

    // Measure before mutating so the rail scroll below costs at most one layout pass.
    const rail = this.refs.rail;
    const target = tabs[next];
    let scrollTo = null;

    if (rail instanceof HTMLElement && target && rail.scrollWidth > rail.clientWidth) {
      scrollTo = Math.max(0, target.offsetLeft - (rail.clientWidth - target.offsetWidth) / 2);
    }

    for (const [position, panel] of panels.entries()) {
      const active = position === next;
      panel.classList.toggle('press__panel--active', active);
      panel.toggleAttribute('inert', !active);
    }

    for (const [position, tab] of tabs.entries()) {
      const active = position === next;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      if (commit) tab.tabIndex = active ? 0 : -1;
    }

    if (scrollTo != null) {
      rail?.scrollTo({ left: scrollTo, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    if (commit) this.#current = next;
  }

  /** Records a deliberate interaction, which permanently retires autoplay. */
  #engage() {
    this.#engaged = true;
    this.#stop();
  }

  /** Starts or stops autoplay to match the current state. */
  #sync() {
    const paused =
      this.#engaged ||
      this.#editing ||
      !this.#visible ||
      this.#focused ||
      (this.#hovered && this.dataset.pauseOnHover === 'true');

    if (!this.#canAutoplay || paused) {
      this.#stop();
      return;
    }

    if (this.#timer) return;

    const interval = Number(this.dataset.interval) || 6000;
    this.#timer = setInterval(() => this.#activate(this.#current + 1), interval);
  }

  #stop() {
    if (!this.#timer) return;
    clearInterval(this.#timer);
    this.#timer = null;
  }

  #onPointerEnter = () => {
    this.#hovered = true;
    this.#sync();
  };

  #onPointerLeave = () => {
    this.#hovered = false;
    if (this.dataset.hoverPreview === 'true') this.#activate(this.#current);
    this.#sync();
  };

  #onFocusIn = () => {
    this.#focused = true;
    this.#sync();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    // Moving focus between the rail's own buttons must not restart the timer mid-tick.
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;

    this.#focused = false;
    this.#sync();
  };

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (this.dataset.hoverPreview !== 'true' || event.pointerType !== 'mouse') return;

    const button = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[role="tab"]');
    if (!(button instanceof HTMLElement)) return;

    this.#activate(Number(button.dataset.index), false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const button = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[role="tab"]');
    if (!(button instanceof HTMLElement)) return;

    const from = Number(button.dataset.index);
    let next;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = from + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = from - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#engage();
    this.#activate(next);
    this.#tabs[this.#current]?.focus();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    // Mouse users have the logo rail; swiping is for touch and pen only, so text selection and
    // link clicks inside a panel are never intercepted.
    if (event.pointerType === 'mouse') return;
    this.#swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const start = this.#swipe;
    this.#swipe = null;

    if (!start || start.id !== event.pointerId) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // Ignore anything closer to vertical than horizontal so page scrolling is never hijacked.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dy) > Math.abs(dx)) return;

    this.#engage();
    this.#activate(this.#current + (dx < 0 ? 1 : -1));
  };

  #onPointerCancel = () => {
    this.#swipe = null;
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const panel = this.#panelFor(event);
    if (!panel) return;

    this.#editing = true;
    this.#activate(Number(panel.dataset.index));
    this.#sync();
  };

  /** @param {Event} event */
  #onBlockDeselect = (event) => {
    if (!this.#panelFor(event)) return;

    this.#editing = false;
    this.#sync();
  };

  /**
   * Resolves a theme editor block event to one of this carousel's panels.
   *
   * @param {Event} event
   * @returns {HTMLElement | null}
   */
  #panelFor(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return null;

    const panel = target.closest('.press__panel');

    return panel instanceof HTMLElement && this.contains(panel) ? panel : null;
  }
}

if (!customElements.get('press-carousel-component')) {
  customElements.define('press-carousel-component', PressCarousel);
}
